import { createContext, useCallback, useContext } from 'react';

import { useAuth, User } from '@opendoor/auth-fe';
import { EventTrackingAction } from '@opendoor/observability/slim';

import { GetBwodListingPropertyInfoQuery } from '__generated__/athena';

import { useObservability } from '../../../helpers/observability';
import { DEPLOY_ENV } from '../../globals';
import { getAnonymousId } from '../../shared/trackers';

interface EventData {
  [key: string]: unknown;
}

interface TrackProperty {
  city?: string;
  state?: string;
  postalCode?: string;
  url?: string;
  propertyStatus?: string;
  isBuyDirectEligible?: boolean;
}

// Action names should follow this format
type PropertyEventAction =
  | 'property-view'
  | 'property-favorite'
  | 'property-share'
  | 'property-similar-listings'
  | 'property-purchase-calculator'
  | 'property-images';

export type Property = GetBwodListingPropertyInfoQuery['property']['property'];

interface PropertyAnalyticsProps {
  property?: Property;
}

const initialPropertyContext: PropertyAnalyticsProps = {
  property: undefined,
};

export const PropertyAnalytics = createContext(initialPropertyContext);

export const usePropertyAnalytics = () => useContext(PropertyAnalytics);

/**
 * Geenerate the payload for the trackProperty function, exported for testing
 */
export function buildPayload<T extends EventData>(
  property: Property,
  additionalContextualData: T = {} as T,
  user?: User | null,
) {
  const isBuyDirectEligible = property?.listingMetadata?.tags?.includes('BUY_DIRECT_ELIGIBLE');

  const trackPropertyFields: TrackProperty = {
    ...(property?.city && { city: property?.city }),
    ...(property?.state && { state: property?.state }),
    ...(property?.postalCode && { zip: property?.postalCode }),
    ...(property?.displaySlug && { url: property?.displaySlug }),
    ...(property?.status && { propertyStatus: property?.status }),
    ...(property?.opendoorOwned && { opendoorOwned: property?.opendoorOwned }),
    ...(property?.addressId && { addressId: property?.addressId }),
    isBuyDirectEligible,
  };

  const properties = {
    ...additionalContextualData,
    ...trackPropertyFields,
    anonymousId: getAnonymousId(),
    ...(user?.customerUuid && { customerId: user.customerUuid }),
  };
  // Filter out any keys where the value is empty
  const payload = Object.keys(properties)
    .filter((key) => properties[key])
    .reduce((obj: { [key: string]: unknown }, key) => {
      obj[key] = properties[key];
      return obj;
    }, {});

  return payload;
}

export const useTrackProperty = () => {
  const analytics = useObservability();
  const { property } = usePropertyAnalytics();
  const { user } = useAuth();
  const trackProperty = useCallback(
    function <T extends EventData>(
      featureName: PropertyEventAction,
      label?: string,
      additionalContextualData: T = {} as T,
    ) {
      const action = featureName as EventTrackingAction;
      const payload = buildPayload(property, additionalContextualData, user);
      analytics.trackEvent(action, label, payload);

      if (DEPLOY_ENV === 'development') {
        console.debug(`[Analytics]`, { featureName, label, payload });
      }
    },
    [analytics.trackEvent, property],
  );

  return { trackProperty };
};

export default useTrackProperty;
